import { forwardRef, useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";

import { mergeAriaIds } from "../../utils/aria";
import { BaseInput } from "./BaseInput";
import type {
  BaseInputProps,
  NumberInputCommitMode,
  NumberInputMode,
} from "./foundations/inputTypes";

// In-progress strings that are not parseable numbers yet.
const isTransientNumberValue = (value: string, allowNegative: boolean) =>
  value === "" || value === "." || (allowNegative && (value === "-" || value === "-."));

const getNumberPattern = (
  mode: NumberInputMode,
  allowNegative: boolean,
  maxDecimalPlaces?: number,
): RegExp => {
  const sign = allowNegative ? "-?" : "";
  if (mode === "integer") return new RegExp(`^${sign}\\d*$`);
  const decimal = maxDecimalPlaces != null ? `(?:\\.\\d{0,${maxDecimalPlaces}})?` : "(?:\\.\\d*)?";
  return new RegExp(`^${sign}(?:\\d+)?${decimal}$`);
};

// Treat invalid numbers from callers as empty instead of rendering "NaN"/"Infinity".
const formatExternalValue = (value?: number | null) =>
  typeof value === "number" && Number.isFinite(value) ? String(value) : "";

const stripLeadingZeros = (raw: string): string => {
  const sign = raw.startsWith("-") ? "-" : "";
  const body = sign ? raw.slice(1) : raw;
  const dotIndex = body.indexOf(".");
  const intPart = dotIndex === -1 ? body : body.slice(0, dotIndex);
  const fracPart = dotIndex === -1 ? "" : body.slice(dotIndex); // "." 포함
  if (intPart === "") return raw;
  const trimmedInt = intPart.replace(/^0+/, "");
  const newInt = trimmedInt === "" ? "0" : trimmedInt;
  return newInt === intPart ? raw : `${sign}${newInt}${fracPart}`;
};

const canonicalizeNumberOnCommit = (raw: string): string => {
  const sign = raw.startsWith("-") ? "-" : "";
  const original = sign ? raw.slice(1) : raw;
  let body = original;
  if (/^\.\d+$/.test(body)) {
    body = `0${body}`;
  } else if (body.length > 1 && body.endsWith(".")) {
    body = body.slice(0, -1);
  }
  return body === original ? raw : `${sign}${body}`;
};

const Wrapper = styled.div<{ $fullWidth?: boolean }>`
  width: ${({ $fullWidth = true }) => ($fullWidth ? "100%" : "auto")};
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const HintText = styled.span`
  font-size: ${({ theme }) => theme.typography.size.caption};
  color: ${({ theme }) => theme.color.text.danger};
  padding-left: 0.2rem;
`;

export interface NumberInputProps extends Omit<
  BaseInputProps,
  "type" | "value" | "defaultValue" | "onChange"
> {
  /** controlled 숫자 값. `null`(또는 미지정) = 빈 상태. */
  value?: number | null;
  /** `(parsedValue, rawString)` — parsedValue는 transient 상태(`""`/`"."` 등)에서 `null`. */
  onChange?: (value: number | null, rawValue: string) => void;
  /** `"integer"`(소수점 차단) / `"decimal"`(기본). */
  mode?: NumberInputMode;
  /** false면 빈 값을 0(또는 직전 value)으로 복원하고, 부모가 빈 상태였으면 복원값을 `onChange`로 emit. 기본 true. */
  allowEmpty?: boolean;
  /** 파싱 값 emit 시점 — `"change"`(기본) / `"blur"`. */
  commitMode?: NumberInputCommitMode;
  /** false면 음수 입력 차단. 기본 true. */
  allowNegative?: boolean;
  /** 소수점 이하 최대 자릿수 (초과 입력 차단). */
  maxDecimalPlaces?: number;
  /**
   * Validation hint rendered below the input and linked with aria-describedby.
   * Pass `invalid` separately for the red border and aria-invalid.
   */
  validationHint?: string;
}

/**
 * Controlled numeric input backed by `<input type="text">` so transient values
 * like `-`, `.`, and `1.` can be preserved while editing.
 * 라벨이 필요하면 `<Field>`로 감싸세요 — 자체 `label` prop은 없습니다.
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      onChange,
      mode = "decimal",
      allowEmpty = true,
      commitMode = "change",
      allowNegative = true,
      maxDecimalPlaces,
      validationHint,
      fullWidth = true,
      inputMode,
      onEnterPress,
      onBlur,
      className,
      style,
      "aria-describedby": ariaDescribedBy,
      ...props
    },
    ref,
  ) => {
    const [rawValue, setRawValue] = useState(formatExternalValue(value));
    const pattern = getNumberPattern(mode, allowNegative, maxDecimalPlaces);
    const generatedHintId = useId();
    const hintId = validationHint ? generatedHintId : undefined;
    const describedBy = mergeAriaIds(ariaDescribedBy, hintId);
    const lastFinalizedRef = useRef<string | null>(null);

    useEffect(() => {
      setRawValue((current) => {
        // Preserve a parent echo of the value we just emitted so transient editing
        // strings are not overwritten by their parsed numeric value.
        const formatted = formatExternalValue(value);
        if (formatted === current) return current;
        if (
          value != null &&
          !isTransientNumberValue(current, allowNegative) &&
          Number(current) === value
        ) {
          return current;
        }
        if (value == null && isTransientNumberValue(current, allowNegative)) return current;
        return formatted;
      });
    }, [value, allowNegative]);

    const emitParsedValue = (nextRawValue: string) => {
      if (isTransientNumberValue(nextRawValue, allowNegative)) {
        if (allowEmpty) onChange?.(null, nextRawValue);
        return;
      }
      const nextValue = Number(nextRawValue);
      if (Number.isNaN(nextValue)) return;
      onChange?.(nextValue, nextRawValue);
    };

    const finalizeValue = (rawInput: string) => {
      const nextRawValue = canonicalizeNumberOnCommit(rawInput);
      const canonicalized = nextRawValue !== rawInput;
      const isRestoring = !allowEmpty && isTransientNumberValue(nextRawValue, allowNegative);
      const finalizedRaw = isRestoring ? formatExternalValue(value ?? 0) : nextRawValue;
      if (lastFinalizedRef.current === finalizedRaw) return;
      lastFinalizedRef.current = finalizedRaw;
      if (isRestoring) {
        setRawValue(finalizedRaw);
        if (value == null) onChange?.(value ?? 0, finalizedRaw);
        return;
      }
      if (canonicalized) setRawValue(nextRawValue);
      if (commitMode === "blur" || canonicalized) emitParsedValue(nextRawValue);
    };

    const input = (
      <BaseInput
        ref={ref}
        fieldControlName="NumberInput"
        type="text"
        inputMode={inputMode ?? (mode === "integer" ? "numeric" : "decimal")}
        value={rawValue}
        fullWidth={fullWidth}
        onChange={(event) => {
          const nextRawValue = event.target.value;
          if (!pattern.test(nextRawValue)) return;
          const normalizedRawValue = stripLeadingZeros(nextRawValue);
          lastFinalizedRef.current = null;
          setRawValue(normalizedRawValue);
          if (commitMode === "change") emitParsedValue(normalizedRawValue);
        }}
        onEnterPress={(event) => {
          finalizeValue(rawValue);
          onEnterPress?.(event);
        }}
        onBlur={(event) => {
          finalizeValue(rawValue);
          onBlur?.(event);
        }}
        className={validationHint ? undefined : className}
        style={validationHint ? undefined : style}
        aria-describedby={describedBy}
        {...props}
      />
    );

    if (validationHint) {
      return (
        <Wrapper $fullWidth={fullWidth} className={className} style={style}>
          {input}
          <HintText id={hintId}>{validationHint}</HintText>
        </Wrapper>
      );
    }

    return input;
  },
);

NumberInput.displayName = "NumberInput";
